// ════════════════════════════════════════════════════════════════════
// Glass-i — Cutting Planner · shared full piece-grid columns.
// One column set for BOTH the planner build grid and the tracker detail modal,
// so the two never drift. Consumers append their own trailing column (Remove /
// Status). Dimension variants (in / cm / CNC) are DERIVED here from mm + edge —
// nothing extra is stored.
//
// Columns are GROUPED into a two-row header (nested headers) that mirrors the
// Proforma line grid: a top band names the family (Order · Glass · Size · CNC ·
// Area · Finish · Detail) and the leaf row names each column. Grouping is header-
// only — it never changes column order, width, or the horizontal scroll span.
// The Order band is frozen left (identity stays visible while the dimension
// columns scroll), exactly as Proforma freezes its #/TYPE/ITEM-DESC band.
// ════════════════════════════════════════════════════════════════════

import dayjs from 'dayjs';
import { isCep, cncDimension, formatThickness, thicknessChip, EDGE_CHIP, mmCeil, mmToInchFraction, stockDisplayName } from './cuttingPlannerHelpers';

// Self-contained colour pill (a plain span — NOT an AntD Tag) so the global
// "tint the whole cell" grid rule leaves the cell alone and no column band forms.
function Pill({ bg, fg, children }) {
  return (
    <span className="inline-block rounded px-1.5 leading-[18px] text-[11px] font-semibold tabular-nums"
      style={{ background: bg, color: fg }}>{children}</span>
  );
}

// mm is always displayed rounded UP to the whole millimetre, and every derived
// dimension (inch fraction, cm, CNC) is taken from that same rounded-up value so
// the row is internally consistent.
const mm = (v) => { const n = mmCeil(v); return n == null ? '—' : n; };
const inch = (v) => { const n = mmCeil(v); return n == null ? '—' : mmToInchFraction(n); };
const cm = (v) => { const n = mmCeil(v); return n == null ? '—' : (n / 10).toFixed(1); };
const cnc = (v, cep) => { const n = mmCeil(v); return n == null ? '—' : cncDimension(n, cep); };
const n3 = (v) => (v == null ? '—' : Number(v).toFixed(3));
const cepOf = (r) => isCep(r.itemDesc, r.edgeFinish);

// A leaf column that tints ONLY its header cell (not the body) so a group band
// can carry a subtle colour without painting a full-height column stripe — the
// same restraint the Proforma grid uses for its ch/pr/fab header tints.
function headTint(cls) { return () => ({ className: cls }); }

// The full piece grid, grouped into nested-header bands. Consumers spread this
// and append a trailing Remove / Status column (which spans both header rows).
export function buildPieceColumns() {
  return [
    // ── Order (frozen left — identity stays put while dimensions scroll) ──
    { title: 'Order', key: 'g-order', fixed: 'left', align: 'center', className: 'cpg-grp', children: [
      { title: 'WO No', dataIndex: 'workOrderNo', key: 'wono', width: 120, ellipsis: true,
        render: (v) => <span className="font-semibold text-slate-700">{v || '—'}</span> },
      { title: 'WO Date', dataIndex: 'workOrderDate', key: 'wodate', width: 96,
        render: (v) => v ? dayjs(v).format('DD MMM YY') : '—' },
      { title: 'PI No', dataIndex: 'proformaNo', key: 'pino', width: 120, ellipsis: true, render: (v) => v || '—' }
      // Customer column hidden — its width is folded into Stock below.
    ] },
    // ── Glass ──
    { title: 'Glass', key: 'g-glass', align: 'center', className: 'cpg-grp', children: [
      { title: 'Stock', dataIndex: 'stockName', key: 'stock', width: 270, ellipsis: true,
        render: (v) => <span title={stockDisplayName(v) || ''}>{stockDisplayName(v) || '—'}</span> },
      { title: 'Thk', dataIndex: 'thickness', key: 'thick', width: 56, align: 'center',
        render: (v) => (v == null ? '—' : <Pill {...thicknessChip(v)}>{formatThickness(v)}</Pill>) },
      { title: 'Type', dataIndex: 'glassType', key: 'type', width: 92, ellipsis: true, render: (v) => v || '—' }
    ] },
    // ── Size · mm ──
    { title: 'Size · mm', key: 'g-mm', align: 'center', className: 'cpg-grp', children: [
      { title: 'W mm', dataIndex: 'widthMm', key: 'wmm', width: 72, align: 'right', render: (v) => <span className="tabular-nums">{mm(v)}</span> },
      { title: 'H mm', dataIndex: 'heightMm', key: 'hmm', width: 72, align: 'right', render: (v) => <span className="tabular-nums">{mm(v)}</span> }
    ] },
    // ── Size · inch ──
    { title: 'Size · inch', key: 'g-in', align: 'center', className: 'cpg-grp', children: [
      { title: 'W in', key: 'win', width: 80, align: 'right', render: (_v, r) => <span className="tabular-nums">{inch(r.widthMm)}</span> },
      { title: 'H in', key: 'hin', width: 80, align: 'right', render: (_v, r) => <span className="tabular-nums">{inch(r.heightMm)}</span> }
    ] },
    // ── Size · cm ──
    { title: 'Size · cm', key: 'g-cm', align: 'center', className: 'cpg-grp', children: [
      { title: 'W cm', key: 'wcm', width: 64, align: 'right', render: (_v, r) => <span className="tabular-nums">{cm(r.widthMm)}</span> },
      { title: 'H cm', key: 'hcm', width: 64, align: 'right', render: (_v, r) => <span className="tabular-nums">{cm(r.heightMm)}</span> }
    ] },
    // ── CNC · mm (edge-compensated cut size) ──
    { title: 'CNC · mm', key: 'g-cnc', align: 'center', className: 'cpg-grp cpg-grp--cnc', children: [
      { title: 'CNC W', key: 'cncw', width: 74, align: 'right', onHeaderCell: headTint('cpg-h-cnc'),
        render: (_v, r) => <span className="tabular-nums text-brand-600">{cnc(r.widthMm, cepOf(r))}</span> },
      { title: 'CNC H', key: 'cnch', width: 74, align: 'right', onHeaderCell: headTint('cpg-h-cnc'),
        render: (_v, r) => <span className="tabular-nums text-brand-600">{cnc(r.heightMm, cepOf(r))}</span> }
    ] },
    // ── Area ──
    { title: 'Area', key: 'g-area', align: 'center', className: 'cpg-grp cpg-grp--area', children: [
      { title: 'Qty', dataIndex: 'qty', key: 'qty', width: 52, align: 'center', onHeaderCell: headTint('cpg-h-area'),
        render: (v) => <span className="tabular-nums">{v ?? 1}</span> },
      { title: 'SqFt', dataIndex: 'sqft', key: 'sqft', width: 72, align: 'right', onHeaderCell: headTint('cpg-h-area'),
        render: (v) => <span className="tabular-nums">{n3(v)}</span> },
      { title: 'SqMt', dataIndex: 'sqmt', key: 'sqmt', width: 72, align: 'right', onHeaderCell: headTint('cpg-h-area'),
        render: (v) => <span className="tabular-nums">{n3(v)}</span> }
    ] },
    // ── Finish ──
    { title: 'Finish', key: 'g-finish', align: 'center', className: 'cpg-grp cpg-grp--finish', children: [
      { title: 'Edge', dataIndex: 'edgeFinish', key: 'edge', width: 60, align: 'center', onHeaderCell: headTint('cpg-h-finish'),
        render: (v) => v ? <Pill {...EDGE_CHIP}>{v}</Pill> : '—' },
      { title: 'Fab', dataIndex: 'fabInfo', key: 'fab', width: 104, ellipsis: true, onHeaderCell: headTint('cpg-h-finish'),
        render: (v) => v ? <span className="tabular-nums">{v}</span> : '—' }
    ] },
    // ── Detail ──
    { title: 'Detail', key: 'g-detail', align: 'center', className: 'cpg-grp', children: [
      { title: 'Pc No', dataIndex: 'pieceNo', key: 'pcno', width: 60, align: 'center', render: (v) => v || '—' },
      { title: 'Barcode', dataIndex: 'barcode', key: 'barcode', width: 158,
        render: (v) => v ? <code className="erp-grid__code">{v}</code> : '—' },
      { title: 'Remarks', dataIndex: 'remarks', key: 'remarks', width: 150, ellipsis: true, render: (v) => v || '—' },
      { title: 'Item Desc', dataIndex: 'itemDesc', key: 'itemdesc', width: 230, ellipsis: true, render: (v) => v || '—' }
    ] }
  ];
}

// Combined min-width for the horizontal scroll container (+ trailing column).
// Grouping is header-only, so the leaf widths — and this span — are unchanged.
export const PIECE_GRID_SCROLL_X = 2392;
